<template>
  <!-- Tooltip shown ONLY when email is missing -->
  <v-tooltip bottom :disabled="!!email">
    <template v-slot:activator="{ on }">
      <div class="d-flex" style="width: 100%;" v-on="on">

        <!-- Passkey login button -->
        <v-btn
            :disabled="!email || isLoading"
            @click="loginWithPasskey"
            color="default"
            block
            :loading="isLoading"
        >
          <v-icon left>mdi-key</v-icon>
          Sign in with a Passkey
        </v-btn>

      </div>
    </template>

    <!-- Tooltip message -->
    <span>You must enter your email address</span>
  </v-tooltip>
</template>

<script>
export default {
  props: {
    email: {
      type: String,
      required: true,
    },
    errorDialog: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    isLoading: false, // Whether passkey login is in progress
  }),
  mounted() {
    const username = localStorage.getItem('passkey_username');
    if (!this.email && username) {
      this.$emit('setUsername', username);
    }
  },

  methods: {
    async loginWithPasskey() {
      this.isLoading = true;

      const ret = await this.passkeyLogin({
        username: this.email,
      });

      if (ret.success) {
        localStorage.setItem('passkey_username', this.email);
        this.$emit('loginSuccessful');
      } else {

        this.errorDialog.showError({
          errorTitle: "Passkey Error",
          errorMessage: ret.errorMsg,
        })
      }
      this.isLoading = false;
    },
    /**
     * Passkey Login
     */
    async passkeyLogin({username}) {
      if (!window.PublicKeyCredential) {
        this.errorDialog.showError({
          errorTitle: "Browser Not Supported",
          errorMessage: "Your browser does not support passkey authentication.",
        })
        return {success: false, errorMsg: "Browser not supported."};
      }

      try {
        if (!username) throw new Error("Username is required for login.");

        // Step 1: Get challenge from backend
        const {data: challengeData} = await this.$axios.get("auth/passkey/generateChallenge", {
          params: {username},
        });

        if (!challengeData?.challenge) throw new Error("Invalid challenge from server.");
        if (!challengeData?.allowCredentials) throw new Error("No registered passkeys available for this user on this device.");

        // Step 2: Prepare publicKey options
        const publicKeyOptions = {
          challenge: this.base64urlDecode(challengeData.challenge),
          allowCredentials: (challengeData.allowCredentials ?? []).map(cred => ({
            type: cred.type,
            id: this.base64urlDecode(cred.id),
            transports: cred.transports,
          })),
          userVerification: challengeData.userVerification || "preferred",
          rpId: challengeData.rpId,
        };

        // Step 3: Request credential
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 30000);

        let credential;
        try{
          credential = await navigator.credentials.get({
            publicKey: publicKeyOptions,
            signal: abortController.signal,
          });
        }catch(error){
          throw new Error("Device does not contain any registered passkeys, or none were returned.");
        }

        clearTimeout(timeoutId);
        if (!credential) throw new Error("Authentication credential not returned.");

        // Step 4: Serialize response
        const credentialData = {
          id: credential.id,
          rawId: this.base64urlEncode(credential.rawId),
          type: credential.type,
          response: {
            clientDataJSON: this.base64urlEncode(credential.response.clientDataJSON),
            authenticatorData: this.base64urlEncode(credential.response.authenticatorData),
            signature: this.base64urlEncode(credential.response.signature),
            userHandle: credential.response.userHandle
                ? this.base64urlEncode(credential.response.userHandle)
                : null,
          },
        };

        // Step 5: Submit to backend
        const {data} = await this.$axios.post("admin/login", {
          username,
          client_id: 'testclient',
          grant_type: "webauthn",
          challengeResponse: credentialData,
        });

        if(!data.access_token) throw new Error(data.message ?? "Invalid response from server.");


        // Step 6: Persist tokens
        localStorage.setItem('refresh_token', data.refresh_token);
        this.$axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

        return {success: true};
      } catch (error) {
        return {
          success: false,
          errorMsg: error?.response?.data?.message || error?.message || "Login failed.",
        };
      }
    },
    base64urlEncode(buffer) {
      return btoa(String.fromCharCode(...new Uint8Array(buffer)))
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, "");
    },

    base64urlDecode(base64url) {
      const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/") + "==".slice(0, (3 * base64url.length) % 4);
      return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    }
  },
};
</script>

